import { useEffect, useState } from 'react';
import { Box, type SxProps, type Theme } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { publicApi } from '@/api/public.api';
import { storage } from '@/lib/storage';
import { useBranding } from '@/hooks/usePublicBranding';
import { APP_NAME, COMPANY_LOGO } from '@/config/productBrand';

interface Props {
  size?: number;
  /** Used for the fallback initial + alt text (defaults to the tenant company name). */
  name?: string;
  rounded?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * Renders the resolved tenant's logo. The logo is fetched as a blob (so the X-Tenant header is
 * sent) and shown as an <img>; when no logo is configured it falls back to the company initial on a
 * branded mark. Never shows product (system) branding.
 */
export default function BrandLogo({ size = 36, name, rounded = true, sx }: Props) {
  const { companyName, logoFileId } = useBranding();
  const label = name ?? companyName;

  const blob = useQuery({
    queryKey: ['branding-logo-blob', storage.tenantCode],
    queryFn: publicApi.logoBlob,
    enabled: !!logoFileId,
    retry: false,
    staleTime: Infinity,
  });

  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob.data) { setUrl(null); return; }
    const objectUrl = URL.createObjectURL(blob.data);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [blob.data]);

  const base: SxProps<Theme> = {
    width: size, height: size, borderRadius: rounded ? 2 : 0, flexShrink: 0,
  };

  if (url) {
    // The whole logo, never cropped: a wide logo keeps the height and grows sideways (up to 3× the height).
    return <Box component="img" src={url} alt={label}
      sx={{ height: size, width: 'auto', maxWidth: size * 3, borderRadius: rounded ? 2 : 0, flexShrink: 0, objectFit: 'contain', bgcolor: '#fff', p: 0.25, ...sx } as SxProps<Theme>} />;
  }

  // No tenant logo configured: fall back to the GoERP product logo (never a broken image).
  return (
    <Box
      component="img"
      src={COMPANY_LOGO}
      alt={label || APP_NAME}
      sx={{ ...base, objectFit: 'contain', bgcolor: '#fff', p: 0.25, ...sx } as SxProps<Theme>}
    />
  );
}
